/**
 * 報酬セット詳細画面 (CommissionSetCategoriesPage) 下部の「個別設定 (override)」セクション (v3.2.17)。
 *
 * - SA / 社員 単位で個別の報酬テーブル + 個別説明代行料を設定
 * - 一覧 + 「追加」「編集」「削除」操作
 */
import type { CommissionOverride } from '@goenhub/shared';
import { FileText, Pencil, Plus, Trash2, UserCog } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAdmins } from '@/hooks/useAdmins';
import {
  useCommissionOverrides,
  useDeleteCommissionOverride,
  type CommissionOverrideRecord,
} from '@/hooks/useCommissionOverrides';

import { CommissionOverrideDialog } from './CommissionOverrideDialog';
import { CommissionOverrideTableViewerDialog } from './CommissionOverrideTableViewerDialog';

interface Props {
  setId: string;
}

export function CommissionOverridesSection({ setId }: Props) {
  const overrides = useCommissionOverrides(setId);
  const admins = useAdmins();
  const deleteOverride = useDeleteCommissionOverride();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionOverrideRecord | null>(null);
  // v3.2.29+: 報酬表ビューア モーダル
  const [viewerOverrideId, setViewerOverrideId] = useState<string | null>(null);

  // SA / 社員 候補 (AdminRecord は uid フィールドを使うので id 互換に変換)
  // v3.2.18: pending (招待中・未ログイン) は除外。ログイン後 admins/{uid} が作成されるまで
  // 個別設定の作成は意味がない (Cloud Function 側で admin doc を参照するため not-found エラーになる)。
  const eligibleAdmins = useMemo(() => {
    return (admins.data ?? [])
      .filter(
        (a) =>
          a.dept === 'sales' &&
          (a.level === 'external' || a.level === 'employee') &&
          a.resolvedStatus === 'active',
      )
      .map((a) => ({
        id: a.uid,
        fullName: a.fullName,
        displayName: a.displayName,
        email: a.email,
        level: a.level,
      }));
  }, [admins.data]);

  // 既に override がある uid セット
  const existingIds = useMemo(
    () => new Set((overrides.data ?? []).map((o) => o.id)),
    [overrides.data],
  );

  const handleDelete = async (o: CommissionOverrideRecord) => {
    if (!confirm(`${o.recipientName} の個別設定を削除します。\n以降、この受取人はロール標準の報酬設定 (sa / employee リスト) に従います。\nよろしいですか？`)) return;
    try {
      await deleteOverride.mutateAsync({ setId, recipientId: o.id });
      toast.success(`${o.recipientName} の個別設定を削除しました`);
    } catch (err) {
      toast.error(`削除に失敗: ${(err as Error).message}`);
    }
  };

  const handleEdit = (o: CommissionOverrideRecord) => {
    setEditing(o);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-3 mt-6 pt-6 border-t border-[var(--border)]">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <UserCog className="size-4 text-[var(--accent)]" />
            個別設定 (SA / 社員ごとのオーバーライド)
          </h3>
          <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
            特定の SA / 社員にロール標準と異なる報酬テーブルを適用 + 個別説明代行料の設定
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleAdd}
          disabled={eligibleAdmins.length === 0 || existingIds.size >= eligibleAdmins.length}
        >
          <Plus className="size-3.5" />
          個別設定を追加
        </Button>
      </div>

      {(overrides.data ?? []).length === 0 ? (
        <div className="text-center py-6 text-[11px] text-[var(--text-muted)] italic">
          個別設定はありません。「個別設定を追加」から作成できます。
        </div>
      ) : (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="grid grid-cols-[140px_1fr_120px_100px_160px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            <div>種別</div>
            <div>受取人</div>
            <div className="text-right">商品報酬</div>
            <div className="text-right">代行料/件</div>
            <div className="text-center">操作</div>
          </div>
          {(overrides.data ?? []).map((o) => (
            <div
              key={o.id}
              className="grid grid-cols-[140px_1fr_120px_100px_160px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)]"
            >
              <div>
                <Badge variant="default">
                  {o.recipientType === 'sa' ? 'SA (営業パートナー)' : '社員 (営業内勤)'}
                </Badge>
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium">{o.recipientName}</div>
                {o.memo && (
                  <div className="truncate text-[10px] text-[var(--text-muted)]">{o.memo}</div>
                )}
              </div>
              <div className="text-right tabular-nums">
                {o.items.filter((it) => it.value > 0).length} 件設定
              </div>
              <div className="text-right tabular-nums">
                {o.briefingFee && o.briefingFee > 0 ? `¥${o.briefingFee.toLocaleString()}` : '—'}
              </div>
              <div className="flex items-center justify-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewerOverrideId(o.id)}
                  disabled={o.items.filter((it) => it.value > 0).length === 0}
                  title="報酬表をモーダルで表示 / PDF 出力"
                >
                  <FileText className="size-3" />
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => handleEdit(o)} title="編集">
                  <Pencil className="size-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(o)}
                  disabled={deleteOverride.isPending}
                  title="削除 (ロール標準に戻す)"
                >
                  <Trash2 className="size-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CommissionOverrideDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        setId={setId}
        editing={editing as CommissionOverride | null}
        eligibleAdmins={eligibleAdmins}
        existingIds={existingIds}
      />

      {/* v3.2.29+: 個別設定 報酬表ビューア モーダル */}
      {viewerOverrideId && (
        <CommissionOverrideTableViewerDialog
          open={viewerOverrideId !== null}
          onOpenChange={(o) => {
            if (!o) setViewerOverrideId(null);
          }}
          setId={setId}
          overrideId={viewerOverrideId}
        />
      )}
    </div>
  );
}
